/* global $ */
import React, { useState, useEffect, useCallback } from "react";
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import axiosInstance from '../axiosInstance';

const FUEL_OPTIONS = ['Petrol', 'Diesel', 'Electric', 'CNG', 'LPG'];

const BIKE_BRANDS = [
  'Bajaj', 'TVS', 'Royal Enfield', 'Honda', 'Yamaha', 'Hero', 'KTM', 'Suzuki',
  'Triumph', 'Kawasaki', 'Ampere', 'Aprilia', 'Ather', 'Avore', 'Benelli', 'BGauss',
  'BMW', 'Bounce', 'Brixton Motorcycles', 'BSA', 'CFMoto', 'Ducati', 'E3 Electric',
  'Evolet', 'Ferrato', 'Gemopai', 'Harley-Davidson', 'Hop Electric', 'Husqvarna',
  'Indian', 'iVOOMi', 'Jawa', 'Joy e-bike', 'Keeway', 'Kinetic', 'Kinetic Green',
  'Lectrix', 'Matter', 'Moto Guzzi', 'Moto Morini', 'Norton', 'Numeros', 'Oben',
  'Odysse', 'Okinawa', 'OLA', 'OPG Mobility', 'PURE EV', 'QJ Motor',
  'Quantum Energy', 'Raptee HV', 'Revolt', 'River', 'Simple Energy',
  'Ultraviolette', 'Vespa', 'VIDA', 'VLF', 'Yezdi', 'Yo', 'Zontes',
];

const CAR_BRANDS = [
  'Tata', 'Maruti Suzuki', 'Mahindra', 'Hyundai', 'Toyota', 'Kia', 'Skoda',
  'BMW', 'MG', 'Honda', 'Nissan', 'Volkswagen', 'Mercedes-Benz', 'Renault',
  'Land Rover', 'Jeep', 'Audi', 'BYD', 'Citroen', 'VinFast', 'Porsche',
  'Lexus', 'Volvo', 'Mini', 'Force Motors', 'Lamborghini', 'Ferrari', 'Jaguar',
  'Rolls-Royce', 'Isuzu', 'Tesla', 'Maserati', 'Aston Martin', 'McLaren',
  'Bentley', 'Lotus',
];

const emptyRecord = {
  // Vehicle Info
  date: '', reg_no: '', make_year: '', model: '', color: '',
  chase_no: '', engine_no: '', noofowners: '', buy_price: '', sell_price: '', km_driven: '',
  // Registration Details
  vehicle_name: '', reg_date: '', reg_valid_upto: '',
  fuel_type: '', endos: false, endosinrc: '',
  // RC Owner
  rcowner_name: '', rcowner_pan: '', rcowner_aadhaar: '',
  // Insurance
  insurance: '', insurance_upto: '',
  // Financial & Transfer
  financier: '', finance_amount: '', nametransferto: '', nametransferholdingamount: '',
  // Dealer Info
  dealer_name: '', dealer_address1: '', dealer_address2: '', dealer_address3: '',
  dealer_city: '', dealer_phone1: '', dealer_phone2: '', note: '',
  // Marketplace
  isPublished: false,
};

const emptyFiles = {
  aadhar_file: null, aadhar_file_back: null,
  rc_file: null, rc_file_back: null,
  pan_file: null, pan_file_back: null,
  bike_photo_1: null, bike_photo_2: null, bike_photo_3: null, bike_photo_4: null,
};

function Dealer() {
  const [data, setData]               = useState([]);
  const [searchText, setSearchText]   = useState('');
  const [newRecord, setNewRecord]     = useState(emptyRecord);
  const [files, setFiles]             = useState(emptyFiles);
  const [selectedRow, setSelectedRow]   = useState(null);
  const [errors, setErrors]             = useState({});
  const [vehicleType, setVehicleType]   = useState('bike'); // 'bike' | 'car' — UI toggle only
  const [publishing, setPublishing]     = useState(null); // dealer_id being quick-published

  const fetchData = async () => {
    try {
      const res = await axiosInstance.get('/dealer/list');
      setData(res.data || []);
    } catch (err) {
      console.error('Error fetching dealers:', err);
      setData([]);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewRecord(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const { name, files: fl } = e.target;
    setFiles(prev => ({ ...prev, [name]: fl[0] || null }));
  };

  const toggleFuelType = (opt) => {
    const current = newRecord.fuel_type
      ? newRecord.fuel_type.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    const updated = current.includes(opt)
      ? current.filter(f => f !== opt)
      : [...current, opt];
    setNewRecord(prev => ({ ...prev, fuel_type: updated.join(',') }));
  };

  const fuelList = () =>
    newRecord.fuel_type
      ? newRecord.fuel_type.split(',').map(s => s.trim()).filter(Boolean)
      : [];

  const validateForm = () => {
    const errs = {};
    if (!newRecord.reg_no)         errs.reg_no         = 'Registration No is required';
    if (!newRecord.date)           errs.date           = 'Date is required';
    if (!newRecord.make_year)      errs.make_year      = 'Make Year is required';
    if (!newRecord.model)          errs.model          = 'Model is required';
    if (!newRecord.color)          errs.color          = 'Color is required';
    if (!newRecord.noofowners)     errs.noofowners     = 'No. of Owners is required';
    if (!newRecord.buy_price)      errs.buy_price      = 'Buy Price is required';
    if (!newRecord.sell_price)     errs.sell_price     = 'Sell Price is required';
    if (!newRecord.km_driven)      errs.km_driven      = 'KM Driven is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const resetForm = () => {
    setNewRecord(emptyRecord);
    setFiles(emptyFiles);
    setErrors({});
    setSelectedRow(null);
    setVehicleType('bike');
  };

  const hasAnyFile = () => Object.values(files).some(f => f !== null);

  const buildFileFormData = (dealerId, userId, isUpdate) => {
    const fd = new FormData();
    if (isUpdate) {
      fd.append('modified_by', userId);
      fd.append('created_by', userId);
    } else {
      fd.append('dealer_id', dealerId);
      fd.append('created_by', userId);
    }
    Object.entries(files).forEach(([key, file]) => {
      if (file) fd.append(key, file);
    });
    return fd;
  };

  const handleAddButtonClick = () => {
    resetForm();
    $('#addRowModal').modal('show');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const userId = localStorage.getItem('user_id');
    try {
      const res = await axiosInstance.post('/dealer/add', {
        ...newRecord,
        vehicle_type: vehicleType,
        endos: newRecord.endos.toString(),
        isPublished: newRecord.isPublished,
        created_by: userId,
      });
      const dealerId = res.data?.dealer_ids?.[0];
      if (hasAnyFile() && dealerId) {
        await axiosInstance.post('/dealerfile/upload', buildFileFormData(dealerId, userId, false), {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      Swal.fire('Saved!', 'Dealer saved successfully', 'success');
      resetForm();
      $('#addRowModal').modal('hide');
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error saving dealer';
      Swal.fire({ icon: 'error', title: 'Error!', text: msg });
    }
  };

  const handleView = (row) => {
    setSelectedRow(row);
    $('#viewRowModal').modal('show');
  };

  const handleEdit = async (row) => {
    const result = await Swal.fire({
      title: 'Edit this dealer?', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Yes, edit it!',
    });
    if (!result.isConfirmed) return;
    const d = row.dealer_datas || {};

    const isoDate = (raw) => {
      try {
        const s = raw?.substring(0, 10) ?? '';
        return (s.length === 10 && s.substring(0, 4) !== '0000') ? s : '';
      } catch { return ''; }
    };

    setVehicleType(
      d.vehicle_type === 'car' ? 'car'
      : d.vehicle_type === 'bike' ? 'bike'
      : BIKE_BRANDS.includes(d.vehicle_name) ? 'bike'
      : CAR_BRANDS.includes(d.vehicle_name) ? 'car'
      : 'bike'
    );
    setSelectedRow(row);
    setNewRecord({
      date: isoDate(d.date),
      reg_no: row.reg_no || '',
      make_year: d.make_year || '', model: d.model || '', color: d.color || '',
      chase_no: d.chase_no || '', engine_no: d.engine_no || '',
      noofowners: d.noofowners || '', buy_price: d.buy_price || '', sell_price: d.sell_price || '', km_driven: d.km_driven || '',
      vehicle_name: d.vehicle_name || '',
      reg_date: isoDate(d.reg_date), reg_valid_upto: isoDate(d.reg_valid_upto),
      fuel_type: d.fuel_type || '',
      endos: d.endos === true || d.endos === 'true' || d.endos === 1,
      endosinrc: d.endosinrc || '',
      rcowner_name: d.rcowner_name || '', rcowner_pan: d.rcowner_pan || '',
      rcowner_aadhaar: d.rcowner_aadhaar || '',
      insurance: d.insurance || '', insurance_upto: isoDate(d.insurance_upto),
      financier: d.financier || '', finance_amount: d.finance_amount || '',
      nametransferto: d.nametransferto || '',
      nametransferholdingamount: d.nametransferholdingamount || '',
      dealer_name: d.dealer_name || '', dealer_address1: d.dealer_address1 || '',
      dealer_address2: d.dealer_address2 || '', dealer_address3: d.dealer_address3 || '',
      dealer_city: d.dealer_city || '', dealer_phone1: d.dealer_phone1 || '',
      dealer_phone2: d.dealer_phone2 || '', note: d.note || '',
      isPublished: d.isPublished === true || d.isPublished === 1,
    });
    setFiles(emptyFiles);
    $('#editRowModal').modal('show');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRow || !validateForm()) return;
    const result = await Swal.fire({
      title: 'Save changes?', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Yes, save it!',
    });
    if (!result.isConfirmed) return;
    const userId = localStorage.getItem('user_id');
    try {
      await axiosInstance.put(`/dealer/update/${selectedRow.dealer_id}`, {
        id: selectedRow.id,
        ...newRecord,
        vehicle_type: vehicleType,
        endos: newRecord.endos.toString(),
        isPublished: newRecord.isPublished,
        modified_by: userId,
      });
      if (hasAnyFile()) {
        await axiosInstance.put(
          `/dealerfile/update/${selectedRow.dealer_id}`,
          buildFileFormData(null, userId, true),
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      Swal.fire('Saved!', 'Dealer updated successfully', 'success');
      resetForm();
      $('#editRowModal').modal('hide');
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Error updating dealer';
      Swal.fire('Error!', msg, 'error');
    }
  };

  const handleQuickPublish = async (row) => {
    const current = row.dealer_datas?.isPublished === true || row.dealer_datas?.isPublished === 1;
    const action  = current ? 'Unlist' : 'Publish';
    const result  = await Swal.fire({
      title: `${action} this vehicle?`,
      text: current ? 'It will be removed from the marketplace.' : 'It will appear on PickMyWheels marketplace.',
      icon: 'question', showCancelButton: true, confirmButtonText: `Yes, ${action}`,
    });
    if (!result.isConfirmed) return;
    setPublishing(row.dealer_id);
    try {
      await axiosInstance.put(`/dealer/update/${row.dealer_id}`, { isPublished: !current });
      Swal.fire('Done!', current ? 'Vehicle unlisted.' : 'Vehicle published to marketplace!', 'success');
      fetchData();
    } catch (err) {
      Swal.fire('Error!', err.response?.data?.message || 'Failed to update', 'error');
    } finally {
      setPublishing(null);
    }
  };

  const handleDelete = useCallback(async (id) => {
    const result = await Swal.fire({
      title: 'Delete this dealer?', text: "This cannot be undone.", icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!',
    });
    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/dealer/${id}`);
        setData(prev => prev.filter(item => item.dealer_id !== id));
        Swal.fire('Deleted!', 'Dealer deleted successfully', 'success');
        fetchData();
      } catch {
        Swal.fire('Error!', 'Failed to delete dealer', 'error');
      }
    }
  }, []);

  const openFile = (url, label) => {
    if (!url) { alert(`${label} not available`); return; }
    window.open(url, '_blank');
  };

  const filteredData = data.filter(item =>
    String(item.reg_no || '').toLowerCase().includes(searchText.toLowerCase()) ||
    String(item.dealer_datas?.model || '').toLowerCase().includes(searchText.toLowerCase()) ||
    String(item.dealer_datas?.color || '').toLowerCase().includes(searchText.toLowerCase()) ||
    String(item.dealer_datas?.dealer_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
    String(item.dealer_datas?.make_year || '').toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    { name: 'No', selector: (_, i) => i + 1, width: '60px', style: { fontWeight: 800 } },
    { name: 'Reg No', selector: row => row.reg_no || '', sortable: true, width: '160px', style: { fontWeight: 800 } },
    { name: 'Model', selector: row => row.dealer_datas?.model || '', sortable: true, width: '120px' },
    { name: 'Color', selector: row => row.dealer_datas?.color || '', sortable: true, width: '110px' },
    { name: 'Year', selector: row => row.dealer_datas?.make_year || '', sortable: true, width: '80px' },
    { name: 'Buy Price', selector: row => row.dealer_datas?.buy_price ? `₹${row.dealer_datas.buy_price}` : '', sortable: true, width: '110px' },
    { name: 'Dealer', selector: row => row.dealer_datas?.dealer_name || '', sortable: true, width: '130px' },
    {
      name: 'Marketplace',
      cell: row => {
        const pub = row.dealer_datas?.isPublished === true || row.dealer_datas?.isPublished === 1;
        return pub
          ? <span className="badge badge-success"><i className="fas fa-check mr-1"></i>Listed</span>
          : <span className="badge badge-secondary">Not listed</span>;
      },
      width: '110px',
    },
    {
      name: 'Actions',
      cell: row => {
        const pub  = row.dealer_datas?.isPublished === true || row.dealer_datas?.isPublished === 1;
        const busy = publishing === row.dealer_id;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button onClick={() => handleView(row)} className="btn btn-info btn-sm" title="View"><i className="fa fa-eye"></i></button>
            <button onClick={() => openFile(row.files?.aadhar_file?.url, 'Aadhaar')} className="btn btn-secondary btn-sm" title="Aadhaar"><i className="fa fa-id-card"></i></button>
            <button onClick={() => openFile(row.files?.rc_file?.url, 'RC')} className="btn btn-secondary btn-sm" title="RC"><i className="fa fa-book"></i></button>
            <button onClick={() => handleEdit(row)} className="btn btn-primary btn-sm" title="Edit"><i className="fa fa-edit"></i></button>
            <button
              onClick={() => handleQuickPublish(row)}
              disabled={busy}
              className={`btn btn-sm ${pub ? 'btn-warning' : 'btn-success'}`}
              title={pub ? 'Unlist from marketplace' : 'Publish to marketplace'}
            >
              {busy ? <i className="fas fa-spinner fa-spin"></i> : <i className={`fas ${pub ? 'fa-eye-slash' : 'fa-globe'}`}></i>}
            </button>
            <button onClick={() => handleDelete(row.dealer_id)} className="btn btn-danger btn-sm" title="Delete"><i className="fa fa-trash"></i></button>
          </div>
        );
      },
      width: '240px',
    },
  ];

  // ── Form helpers ─────────────────────────────────────────────────────────

  const secHead = (title, icon) => (
    <div className="col-12 mt-2 mb-1">
      <h6 className="text-primary font-weight-bold border-bottom pb-1 mb-0">
        <i className={`${icon} mr-2`}></i>{title}
      </h6>
    </div>
  );

  const fld = (label, name, type = 'text', req = false, span = 6) => (
    <div className={`col-sm-${span}`}>
      <div className="form-group">
        <label className="mb-1">
          {label}{req && <span className="text-danger"> *</span>}
        </label>
        <input
          type={type}
          name={name}
          value={newRecord[name] || ''}
          onChange={handleInputChange}
          className={`form-control form-control-sm${errors[name] ? ' is-invalid' : ''}`}
        />
        {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
      </div>
    </div>
  );

  const fileFld = (label, name, existingUrl) => (
    <div className="col-sm-6">
      <div className="form-group">
        <label className="mb-1">{label}</label>
        <div className="custom-file">
          <input
            type="file" className="custom-file-input"
            name={name} id={`f_${name}`}
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png"
          />
          <label className="custom-file-label" htmlFor={`f_${name}`} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {files[name] ? files[name].name : existingUrl ? existingUrl.split('/').pop() : 'Choose file'}
          </label>
        </div>
        {!files[name] && existingUrl && (
          <small className="text-muted">
            Uploaded: <a href={existingUrl} target="_blank" rel="noopener noreferrer">{existingUrl.split('/').pop()}</a>
          </small>
        )}
      </div>
    </div>
  );

  const handleVehicleTypeToggle = (type) => {
    setVehicleType(type);
    setNewRecord(prev => ({ ...prev, vehicle_name: '' }));
  };

  const formBody = (existingFiles) => {
    const brandList = vehicleType === 'car' ? CAR_BRANDS : BIKE_BRANDS;
    return (
    <div className="row">
      {secHead('Vehicle Information', vehicleType === 'car' ? 'fas fa-car' : 'fas fa-motorcycle')}

      {/* Vehicle type toggle */}
      <div className="col-12 mb-2">
        <div className="btn-group btn-group-sm" role="group">
          <button
            type="button"
            className={`btn ${vehicleType === 'bike' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => handleVehicleTypeToggle('bike')}
          >
            <i className="fas fa-motorcycle mr-1"></i> Bike
          </button>
          <button
            type="button"
            className={`btn ${vehicleType === 'car' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => handleVehicleTypeToggle('car')}
          >
            <i className="fas fa-car mr-1"></i> Car
          </button>
        </div>
      </div>

      {fld('Date *', 'date', 'date', true)}
      {fld('Registration No *', 'reg_no', 'text', true)}
      {fld('Make Year *', 'make_year', 'text', true)}

      {/* Vehicle Name — dropdown switching between bike/car brands */}
      <div className="col-sm-6">
        <div className="form-group">
          <label className="mb-1">Vehicle Name (Brand)</label>
          <select
            name="vehicle_name"
            value={newRecord.vehicle_name || ''}
            onChange={handleInputChange}
            className="form-control form-control-sm"
          >
            <option value="">Select {vehicleType === 'car' ? 'Car' : 'Bike'} Brand</option>
            {brandList.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
      </div>

      {fld('Model *', 'model', 'text', true)}
      {fld('Color *', 'color', 'text', true)}
      {fld('Chassis No', 'chase_no', 'text')}
      {fld('Engine No', 'engine_no', 'text')}
      {fld('No. of Owners *', 'noofowners', 'number', true)}
      {fld('Buy Price (₹) *', 'buy_price', 'number', true)}
      {fld('Sell Price (₹) *', 'sell_price', 'number', true)}
      {fld('KM Driven *', 'km_driven', 'number', true)}
      {fld('Reg Date', 'reg_date', 'date')}
      {fld('Reg Valid Upto', 'reg_valid_upto', 'date')}

      {/* Fuel Type */}
      <div className="col-12">
        <div className="form-group">
          <label className="mb-1">Fuel Type</label>
          <div>
            {FUEL_OPTIONS.map(opt => (
              <div key={opt} className="form-check form-check-inline">
                <input
                  className="form-check-input" type="checkbox"
                  id={`fuel_${opt}`}
                  checked={fuelList().includes(opt)}
                  onChange={() => toggleFuelType(opt)}
                />
                <label className="form-check-label" htmlFor={`fuel_${opt}`}>{opt}</label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Endos */}
      <div className="col-sm-3">
        <div className="form-group">
          <div className="form-check mt-2">
            <input className="form-check-input" type="checkbox" name="endos" id="endos"
              checked={newRecord.endos} onChange={handleInputChange} />
            <label className="form-check-label" htmlFor="endos">Endos</label>
          </div>
        </div>
      </div>
      {newRecord.endos && (
        <div className="col-sm-3">
          <div className="form-group">
            <label className="mb-1">Endos in RC</label>
            <select name="endosinrc" value={newRecord.endosinrc} onChange={handleInputChange} className="form-control form-control-sm">
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>
      )}

      {secHead('RC Owner Details', 'fas fa-user')}
      {fld('RC Owner Name', 'rcowner_name')}
      {fld('PAN No', 'rcowner_pan')}
      {fld('Aadhaar No', 'rcowner_aadhaar')}

      {secHead('Insurance', 'fas fa-shield-alt')}
      <div className="col-sm-6">
        <div className="form-group">
          <label className="mb-1">Insurance Status</label>
          <select name="insurance" value={newRecord.insurance} onChange={handleInputChange} className="form-control form-control-sm">
            <option value="">Select</option>
            <option value="Active">Active</option>
            <option value="Expired">Expired</option>
          </select>
        </div>
      </div>
      {fld('Insurance Valid Upto', 'insurance_upto', 'date')}

      {secHead('Financial & Transfer', 'fas fa-rupee-sign')}
      {fld('Financier', 'financier')}
      {fld('Finance Amount (₹)', 'finance_amount', 'number')}
      {fld('Name Transfer To', 'nametransferto')}
      {fld('Name Transfer Holding Amount (₹)', 'nametransferholdingamount', 'number')}

      {secHead('Dealer Information', 'fas fa-store')}
      {fld('Dealer Name', 'dealer_name', 'text')}
      {fld('Address 1', 'dealer_address1', 'text')}
      {fld('Address 2', 'dealer_address2')}
      {fld('Address 3', 'dealer_address3')}
      {fld('City', 'dealer_city')}
      {fld('Primary Phone', 'dealer_phone1', 'tel')}
      {fld('Secondary Phone', 'dealer_phone2', 'tel')}
      {fld('Note', 'note', 'text', false, 12)}

      {secHead('Marketplace', 'fas fa-globe')}
      <div className="col-12">
        <div className="form-group">
          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id="isPublished"
              name="isPublished"
              checked={!!newRecord.isPublished}
              onChange={handleInputChange}
            />
            <label className="custom-control-label" htmlFor="isPublished">
              <strong>Publish to PickMyWheels Marketplace</strong>
              <span className="d-block text-muted small">
                {newRecord.isPublished
                  ? 'This vehicle will be visible to buyers on the marketplace.'
                  : 'Toggle on to list this vehicle on the public marketplace.'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {secHead('Documents', 'fas fa-paperclip')}
      {fileFld('Aadhaar Front', 'aadhar_file',      existingFiles?.aadhar_file?.url)}
      {fileFld('Aadhaar Back',  'aadhar_file_back', existingFiles?.aadhar_file_back?.url)}
      {fileFld('RC Front',      'rc_file',           existingFiles?.rc_file?.url)}
      {fileFld('RC Back',       'rc_file_back',      existingFiles?.rc_file_back?.url)}
      {fileFld('PAN Front',     'pan_file',          existingFiles?.pan_file?.url)}
      {fileFld('PAN Back',      'pan_file_back',     existingFiles?.pan_file_back?.url)}

      {secHead('Vehicle Photos (up to 4)', 'fas fa-camera')}
      {[1, 2, 3, 4].map(i => fileFld(`Bike Photo ${i}`, `bike_photo_${i}`, existingFiles?.[`bike_photo_${i}`]?.url ?? existingFiles?.bikePhotos?.[i - 1]?.url))}
    </div>
  );
  };

  // ── View modal helpers ───────────────────────────────────────────────────

  const vRow = (label, value) => value ? (
    <p className="mb-1 small"><strong>{label}:</strong> {value}</p>
  ) : null;

  const bikePhotoUrls = selectedRow?.files
    ? [1, 2, 3, 4]
        .map(i => selectedRow.files[`bike_photo_${i}`]?.url ?? selectedRow.files.bikePhotos?.[i - 1]?.url)
        .filter(Boolean)
    : [];

  const selectedD = selectedRow?.dealer_datas || {};

  return (
    <div className="content-wrapper">
      {/* Header */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0"><i className="fas fa-store mr-2"></i>Dealer Management</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active">Dealers</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col d-flex justify-content-end">
              <button className="btn btn-primary" onClick={handleAddButtonClick}>
                <i className="fa fa-plus mr-2"></i>Add Dealer
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <input
                type="text" placeholder="Search reg no, model, color, dealer..."
                value={searchText} onChange={e => setSearchText(e.target.value)}
                className="form-control mb-3"
              />
              <DataTable
                columns={columns} data={filteredData}
                pagination paginationPerPage={20}
                paginationRowsPerPageOptions={[20, 50, 100, 200]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── VIEW MODAL ─────────────────────────────────────────────────────── */}
      <div className="modal fade" id="viewRowModal" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header bg-info text-white">
              <h4 className="modal-title">
                <i className="fas fa-motorcycle mr-2"></i>Dealer & Vehicle Details
              </h4>
              <button type="button" className="close text-white" data-dismiss="modal">
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {selectedRow && (
                <>
                  {/* Bike photos */}
                  {bikePhotoUrls.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-primary font-weight-bold">Vehicle Photos</h6>
                      <div className="row">
                        {bikePhotoUrls.map((url, i) => (
                          <div key={i} className="col-6 col-md-3 mb-2">
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt={`Bike ${i + 1}`} className="img-fluid rounded"
                                style={{ height: 130, width: '100%', objectFit: 'cover' }} />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Header row */}
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <p className="mb-0"><strong>Date:</strong> {selectedD.date || '--'}</p>
                    </div>
                    <div className="col-md-6 text-right">
                      <h5 className="mb-0">
                        <span className="badge badge-info p-2">Reg No: {selectedRow.reg_no || '--'}</span>
                      </h5>
                    </div>
                  </div>

                  <div className="row">
                    {/* Vehicle */}
                    <div className="col-md-6 mb-3">
                      <div className="card card-info card-outline">
                        <div className="card-header py-2"><h6 className="card-title mb-0"><i className="fas fa-motorcycle mr-2"></i>Vehicle Details</h6></div>
                        <div className="card-body py-2">
                          {vRow('Vehicle Name', selectedD.vehicle_name)}
                          {vRow('Model', selectedD.model)}
                          {vRow('Make Year', selectedD.make_year)}
                          {vRow('Color', selectedD.color)}
                          {vRow('Chassis No', selectedD.chase_no)}
                          {vRow('Engine No', selectedD.engine_no)}
                          {vRow('No. of Owners', selectedD.noofowners)}
                          {vRow('Fuel Type', selectedD.fuel_type)}
                          {vRow('Buy Price', selectedD.buy_price ? `₹${selectedD.buy_price}` : null)}
                          {vRow('Sell Price', selectedD.sell_price ? `₹${selectedD.sell_price}` : null)}
                          {vRow('KM Driven', selectedD.km_driven ? `${selectedD.km_driven} km` : null)}
                        </div>
                      </div>
                    </div>

                    {/* Dealer */}
                    <div className="col-md-6 mb-3">
                      <div className="card card-success card-outline">
                        <div className="card-header py-2"><h6 className="card-title mb-0"><i className="fas fa-store mr-2"></i>Dealer Details</h6></div>
                        <div className="card-body py-2">
                          {vRow('Name', selectedD.dealer_name)}
                          {vRow('Address 1', selectedD.dealer_address1)}
                          {vRow('Address 2', selectedD.dealer_address2)}
                          {vRow('Address 3', selectedD.dealer_address3)}
                          {vRow('City', selectedD.dealer_city)}
                          {vRow('Phone 1', selectedD.dealer_phone1)}
                          {vRow('Phone 2', selectedD.dealer_phone2)}
                        </div>
                      </div>
                    </div>

                    {/* Registration */}
                    <div className="col-md-6 mb-3">
                      <div className="card card-warning card-outline">
                        <div className="card-header py-2"><h6 className="card-title mb-0"><i className="fas fa-file-alt mr-2"></i>Registration</h6></div>
                        <div className="card-body py-2">
                          {vRow('Reg Date', selectedD.reg_date)}
                          {vRow('Valid Upto', selectedD.reg_valid_upto)}
                          {(selectedD.endos === true || selectedD.endos === 'true' || selectedD.endos === 1) && vRow('Endos', 'Yes')}
                          {(selectedD.endos === true || selectedD.endos === 'true' || selectedD.endos === 1) && vRow('Endos in RC', selectedD.endosinrc)}
                        </div>
                      </div>
                    </div>

                    {/* RC Owner */}
                    <div className="col-md-6 mb-3">
                      <div className="card card-secondary card-outline">
                        <div className="card-header py-2"><h6 className="card-title mb-0"><i className="fas fa-user mr-2"></i>RC Owner</h6></div>
                        <div className="card-body py-2">
                          {vRow('Name', selectedD.rcowner_name)}
                          {vRow('PAN No', selectedD.rcowner_pan)}
                          {vRow('Aadhaar No', selectedD.rcowner_aadhaar)}
                        </div>
                      </div>
                    </div>

                    {/* Insurance */}
                    <div className="col-md-6 mb-3">
                      <div className="card card-danger card-outline">
                        <div className="card-header py-2"><h6 className="card-title mb-0"><i className="fas fa-shield-alt mr-2"></i>Insurance</h6></div>
                        <div className="card-body py-2">
                          {vRow('Status', selectedD.insurance)}
                          {vRow('Valid Upto', selectedD.insurance_upto)}
                        </div>
                      </div>
                    </div>

                    {/* Financial */}
                    {(selectedD.financier || selectedD.finance_amount || selectedD.nametransferto || selectedD.nametransferholdingamount) && (
                      <div className="col-md-6 mb-3">
                        <div className="card card-primary card-outline">
                          <div className="card-header py-2"><h6 className="card-title mb-0"><i className="fas fa-rupee-sign mr-2"></i>Financial & Transfer</h6></div>
                          <div className="card-body py-2">
                            {vRow('Financier', selectedD.financier)}
                            {vRow('Finance Amount', selectedD.finance_amount ? `₹${selectedD.finance_amount}` : null)}
                            {vRow('Name Transfer To', selectedD.nametransferto)}
                            {vRow('Holding Amount', selectedD.nametransferholdingamount ? `₹${selectedD.nametransferholdingamount}` : null)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Documents */}
                    <div className="col-12 mb-3">
                      <div className="card card-outline">
                        <div className="card-header py-2"><h6 className="card-title mb-0"><i className="fas fa-paperclip mr-2"></i>Documents</h6></div>
                        <div className="card-body py-2">
                          <div className="row">
                            {[
                              ['Aadhaar Front', 'aadhar_file'],      ['Aadhaar Back', 'aadhar_file_back'],
                              ['RC Front',      'rc_file'],           ['RC Back',     'rc_file_back'],
                              ['PAN Front',     'pan_file'],          ['PAN Back',    'pan_file_back'],
                            ].map(([label, key]) => (
                              <div key={key} className="col-sm-4 mb-1 small">
                                <strong>{label}:</strong>{' '}
                                {selectedRow.files?.[key]?.url
                                  ? <a href={selectedRow.files[key].url} target="_blank" rel="noopener noreferrer">View</a>
                                  : <span className="text-muted">Not uploaded</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Note */}
                    {selectedD.note && (
                      <div className="col-12">
                        <div className="alert alert-warning mb-2">
                          <strong><i className="fas fa-sticky-note mr-2"></i>Note:</strong> {selectedD.note}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── ADD MODAL ──────────────────────────────────────────────────────── */}
      <div className="modal fade" id="addRowModal">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title"><i className="fas fa-plus-circle mr-2"></i>Add Dealer</h4>
              <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} id="addDealerForm">
                {formBody(null)}
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-dismiss="modal">Cancel</button>
              <button type="submit" form="addDealerForm" className="btn btn-primary">
                <i className="fas fa-save mr-2"></i>Save Dealer
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ─────────────────────────────────────────────────────── */}
      <div className="modal fade" id="editRowModal">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title"><i className="fas fa-edit mr-2"></i>Edit Dealer</h4>
              <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleFormSubmit} id="editDealerForm">
                {formBody(selectedRow?.files)}
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-dismiss="modal">Cancel</button>
              <button type="submit" form="editDealerForm" className="btn btn-primary">
                <i className="fas fa-save mr-2"></i>Update Dealer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dealer;
